import React, { useState, useEffect, useRef, useMemo } from "react";
import { useProfile } from "../../../contexts/ProfileContext";

import ChatInput from "../CustomerServiceChat/ChatInput";

//Context
import config from "../../../config";
import { IoIosArrowBack } from "react-icons/io";
import ChatSearch from "../CustomerServiceChat/InputSearch";
import customer_avatar from "../../../assets/images/customer_avatar.png";
import { getApi } from "../../AppApis";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ImagePreviewModal from "../../Templates/ImagePreviewModal";
import { MediaDocumentModal } from "../../../components/Modal/MediaDocumentModal/MediaDocumentModal";
import { IoPricetagsSharp } from "react-icons/io5";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import ShowerOutlinedIcon from "@mui/icons-material/ShowerOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import { formatUSAPrice } from "../../../helpers/utils";
import { BiLinkExternal } from "react-icons/bi";
import MessageItem from "../MessageItem";

const API_BASE_URL = "http://image.onmls.com";

function UnifiedChat(props) {
    const ref = useRef();
    const location = useLocation();
    const { profileData } = useProfile();

    // Determine chat type based on props
    const isCustomerSupport =
        props.selectedChat === "customerSupport" || props.selectedChat === "property" || props.selectedChat?.conversation_type === "customer_service";

    // For buyer-seller chats, determine receiver user ID
    const receiver_user_id =
        !isCustomerSupport && profileData?.id === props?.sellerDetails?.user_id
            ? props?.sellerDetails?.receiver_user_id
            : props?.sellerDetails?.user_id;

    const [chatMessages, setchatMessages] = useState([]);
    const [senderMsg, setSenderMsg] = useState("");
    const [allMsg, setAllMsg] = useState([]);
    const [shareFileNameDir, setShareFileNameDir] = useState([]);
    const [sharedDocuments, setSharedDocuments] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [openMedia, setOpenMedia] = useState(false);
    const [property, setProperty] = useState({});
    const [media_image, setMedia_image] = useState(null);
    const navigate = useNavigate();
    const [ws, setWs] = useState(null);
    const [isMqttConnected, setIsMqttConnected] = useState(false);

    // Only for buyer-seller chats
    const [otherUserTyping, setOtherUserTyping] = useState(false);
    const [headerImageError, setHeaderImageError] = useState(false);

    // WebSocket reconnection state
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const [connectionStatus, setConnectionStatus] = useState("disconnected"); // 'connected', 'disconnected', 'reconnecting'

    const searchParams = new URLSearchParams(location.search);
    const propertyId = props.sellerDetails?.property_id || props.sellerDetails?.external_property_id || searchParams.get("property_id");

    const reconnectTimeoutRef = useRef(null);
    const maxReconnectAttempts = 10;
    const baseReconnectDelay = 1000; // 1 second

    // Calculate reconnection delay with exponential backoff
    const getReconnectDelay = (attempt) => {
        return Math.min(baseReconnectDelay * Math.pow(2, attempt), 30000); // Max 30 seconds
    };

    // WebSocket connection effect with robust reconnection
    useEffect(() => {
        if (!profileData?.id) return;

        const accessToken = localStorage.getItem("authUser");
        const token = accessToken?.replace(/^"|"$/g, "");
        const wsUrl = `wss://admin.onmls.com/api/messaging/ws/user/${profileData.id}?token=${token}`;

        let socket = null;
        let shouldReconnect = true;

        const connect = (attemptNumber = 0) => {
            // Clear any existing timeout
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            // Don't attempt to reconnect if we've exceeded max attempts
            if (attemptNumber >= maxReconnectAttempts) {
                console.error("Max WebSocket reconnection attempts reached");
                setConnectionStatus("disconnected");
                setIsReconnecting(false);
                setReconnectAttempt(0);
                return;
            }

            // Update state for reconnection attempts
            if (attemptNumber > 0) {
                setIsReconnecting(true);
                setConnectionStatus("reconnecting");
                console.log(`WebSocket reconnection attempt ${attemptNumber}/${maxReconnectAttempts}`);
            }

            setReconnectAttempt(attemptNumber);

            try {
                socket = new WebSocket(wsUrl);

                socket.onopen = () => {
                    console.log("WebSocket connected successfully");
                    setIsMqttConnected(true);
                    setWs(socket);
                    setConnectionStatus("connected");
                    setIsReconnecting(false);
                    setReconnectAttempt(0);

                    // Retry any pending messages
                    const pendingMessage = localStorage.getItem("pendingMessage");
                    if (pendingMessage) {
                        try {
                            const message = JSON.parse(pendingMessage);
                            setTimeout(() => {
                                if (socket && socket.readyState === WebSocket.OPEN) {
                                    socket.send(JSON.stringify(message));
                                    localStorage.removeItem("pendingMessage");
                                }
                            }, 500); // Small delay to ensure connection is stable
                        } catch (error) {
                            console.error("Failed to retry pending message:", error);
                            localStorage.removeItem("pendingMessage");
                        }
                    }
                };

                socket.onmessage = (message) => {
                    try {
                        const payload = JSON.parse(message.data);
                        console.log("Unified Chat DATA----", { data: props.sellerDetails, props: props.selectedChat, message: payload });
                        // Handle typing status messages (buyer-seller only)
                        if (!isCustomerSupport && payload.type === "typing_status") {
                            setOtherUserTyping(payload.is_typing);
                            return;
                        }

                        // Handle regular messages
                        const newPayload = {
                            ...payload,
                            timestamp: payload.timestamp || payload.sent_at || new Date().toISOString(),
                        };

                        // Deduplicate: Check if this message already exists to prevent duplicates
                        setAllMsg((prev) => {
                            const isDuplicate = prev.some((existingMsg) => {
                                // If both have IDs, compare them
                                if (newPayload.id && existingMsg.id) {
                                    return newPayload.id === existingMsg.id;
                                }
                                // Otherwise check message content, sender, and time window (within 5 seconds)
                                const sameMessage = existingMsg.message === newPayload.message;
                                const sameSender = existingMsg.sender_id === newPayload.sender_id;
                                const existingTime = new Date(existingMsg.timestamp || existingMsg.sent_at || 0).getTime();
                                const newTime = new Date(newPayload.timestamp || newPayload.sent_at || 0).getTime();
                                const withinTimeWindow = Math.abs(existingTime - newTime) < 5000;

                                return sameMessage && sameSender && withinTimeWindow;
                            });

                            if (isDuplicate) {
                                return prev;
                            }
                            
                            // Update chat list silently when new message arrives
                            if (props.updateChatListSilently) {
                                setTimeout(() => props.updateChatListSilently(), 500);
                            }
                            
                            return [...prev, newPayload];
                        });
                        scrolltoBottom();

                        // Stop typing indicator when message is received (buyer-seller only)
                        if (!isCustomerSupport) {
                            setOtherUserTyping(false);
                        }
                    } catch (error) {
                        console.error("Error parsing WebSocket message:", error);
                    }
                };

                socket.onerror = (error) => {
                    console.error("WebSocket error:", error);
                    setConnectionStatus("disconnected");
                };

                socket.onclose = (event) => {
                    console.log(`WebSocket closed. Code: ${event.code}, Reason: ${event.reason}`);
                    setIsMqttConnected(false);
                    setWs(null);
                    setConnectionStatus("disconnected");

                    // Only attempt reconnection if we should reconnect and haven't exceeded max attempts
                    if (shouldReconnect && attemptNumber < maxReconnectAttempts) {
                        const delay = getReconnectDelay(attemptNumber);
                        console.log(`Scheduling WebSocket reconnection in ${delay}ms...`);

                        reconnectTimeoutRef.current = setTimeout(() => {
                            connect(attemptNumber + 1);
                        }, delay);
                    }
                };
            } catch (error) {
                console.error("Failed to create WebSocket connection:", error);
                setConnectionStatus("disconnected");

                // Retry connection after delay
                if (shouldReconnect && attemptNumber < maxReconnectAttempts) {
                    const delay = getReconnectDelay(attemptNumber);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect(attemptNumber + 1);
                    }, delay);
                }
            }
        };

        // Start initial connection
        connect(0);

        return () => {
            shouldReconnect = false;
            if (socket) {
                socket.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            setWs(null);
            setIsMqttConnected(false);
            setIsReconnecting(false);
            setConnectionStatus("disconnected");
        };
    }, [isCustomerSupport, profileData?.id]);

    // Manual reconnection function
    const manualReconnect = () => {
        setReconnectAttempt(0);
        setIsReconnecting(false);
        setConnectionStatus("reconnecting");

        if (ws) {
            ws.close();
        }
        
        // Trigger reconnection by changing a dependency
        setWs(null);
        setIsMqttConnected(false);
    };

    // Force WebSocket reconnection with new data (without reloading chat list)
    const refreshWebSocketConnection = () => {
        console.log("Refreshing WebSocket connection with new data...");
        
        if (ws) {
            ws.close();
        }
        
        // Reset connection state to trigger a fresh connection
        setWs(null);
        setIsMqttConnected(false);
        setConnectionStatus("reconnecting");
        setReconnectAttempt(0);
        setIsReconnecting(false);
        
        // Clear any pending reconnection timeouts
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    };

    // Refresh WebSocket when chat selection changes (but don't reload chat list)
    useEffect(() => {
        if (props.selectedChat && profileData?.id) {
            // Only refresh if we have a valid chat selection and WebSocket is connected
            if (props.selectedChat !== "customerSupport" && props.selectedChat?.id && ws) {
                console.log("Chat selection changed, refreshing WebSocket connection for new conversation...");
                // Small delay to allow current operations to complete
                setTimeout(() => {
                    refreshWebSocketConnection();
                }, 200);
            }
        }
    }, [props.selectedChat?.id, profileData?.id]);

    // Mark conversation as read (customer support only)
    useEffect(() => {
        if (isCustomerSupport && ws && ws.readyState === WebSocket.OPEN && props.selectedChat?.id) {
            ws.send(
                JSON.stringify({
                    type: "mark_seen",
                    conversation_id: props.selectedChat?.id,
                })
            );
        }
    }, [ws, isMqttConnected, isCustomerSupport, props.selectedChat?.id]);

    // admin message
    const addMessage = (message, type) => {
        var messageObj = null;

        switch (type) {
            case "textMessage":
                messageObj = {
                    message: message,
                    timestamp: new Date().toISOString(),
                };
                break;

            case "fileMessage":
                messageObj = {
                    media_file: message,
                    timestamp: new Date().toISOString(),
                };
                break;
            case "media_msg":
                messageObj = {
                    media_file: message,
                    timestamp: new Date().toISOString(),
                };
                break;
            default:
                break;
        }

        setchatMessages([...chatMessages, messageObj]);
        setSenderMsg((pre) => {
            return { ...pre, [type]: messageObj };
        });

        // For buyer-seller chats, also add to allMsg with deduplication
        if (!isCustomerSupport) {
            setAllMsg((prevMsg) => {
                const isDuplicate = prevMsg.some((existingMsg) => {
                    const sameMessage = existingMsg.message === messageObj.message;
                    const existingTime = new Date(existingMsg.timestamp || existingMsg.sent_at || 0).getTime();
                    const newTime = new Date(messageObj.timestamp || 0).getTime();
                    const withinTimeWindow = Math.abs(existingTime - newTime) < 5000;
                    return sameMessage && withinTimeWindow;
                });

                if (isDuplicate) {
                    return prevMsg;
                }
                return [...prevMsg, messageObj];
            });
        }

        scrolltoBottom();
    };

    const scrolltoBottom = () => {
        if (ref.current && ref.current.scrollTo) {
            setTimeout(() => {
                try {
                    ref.current.scrollTo({
                        top: ref.current.scrollHeight,
                        behavior: "smooth",
                    });
                } catch (error) {
                    console.warn("ScrollTo failed:", error);
                }
            }, 100);
        }
    };

    // Chat message sending logic
    const handleChatMessage = async () => {
        try {
            let messageToSend = senderMsg?.textMessage?.message;

            // Add user details for first messages
            if (allMsg?.length === 1 && allMsg[0].message.includes("how can we help you with")) {
                const propertyAddress = localStorage.getItem("propertyAddress");
                const latestProperty = JSON.parse(localStorage.getItem("latestProperty"));
                messageToSend = `${messageToSend}\n User Name: ${profileData?.first_name}, Contact details: **${profileData?.email}, ${profileData?.phone}** , Property Link:${"https://admin.airebrokers.com/public?propertyId=" + latestProperty.ListingId} \n\n---\n **Property Details:** ${propertyAddress}, $${latestProperty?.price}, ${latestProperty?.beds} beds, ${latestProperty?.baths} baths, ${latestProperty?.size || "N/A"} sqft, ${latestProperty?.built_in} year built, ${latestProperty?.ListingId}`;
            } else if (allMsg?.length === 0) {
                messageToSend = `${messageToSend}\n User Name: ${profileData?.first_name}, Contact details: **${profileData?.email}, ${profileData?.phone}**`;
            }

            let message = {
                type: "send_message",
                message: messageToSend,
                message_type: "text",
            };

            // Add conversation ID for buyer-seller chats
            if (!isCustomerSupport) {
                message.conversation_id = props.sellerDetails?.id;
                if (receiver_user_id && profileData?.id !== receiver_user_id) {
                    message.receiver_user_id = receiver_user_id;
                }
            }

            // Add file data if file is selected
            if (media_image) {
                message.media_url = media_image.base64String;
                message.file_name = media_image.fileName;
                message.file_type = "image";
                message.message_type = "image";
            }
            if (propertyId) {
                message.property_id = propertyId;
            }

            console.log("message sent:", message);
            if (!ws || ws.readyState !== WebSocket.OPEN) {
                console.error("WebSocket is not connected, attempting to reconnect...");
                // Trigger a manual reconnection
                manualReconnect();
                // Store the message to retry after reconnection
                localStorage.setItem("pendingMessage", JSON.stringify(message));
                return;
            } else {
                ws.send(JSON.stringify(message));
                setMedia_image(null);
                // Clear any pending message on successful send
                localStorage.removeItem("pendingMessage");
                
                // Update chat list silently after sending message
                if (props.updateChatListSilently) {
                    setTimeout(() => props.updateChatListSilently(), 1000);
                }
            }

            setSenderMsg("");
            scrolltoBottom();
        } catch (error) {
            console.error("Error:", error);
        }
    };

    document.title = `Chat | ${config.get("COMPANY_NAME")}`;

    useEffect(() => {
        if (chatMessages?.length > 0) {
            handleChatMessage();
        }
    }, [chatMessages]);

    // Fetch conversation messages
    const fetchData = async () => {
        try {
            let initialMessage;
            if (propertyId) {
                const savedMessage = localStorage.getItem("initialMessage");

                const response = await getApi(`/properties/get_public_property/${propertyId}`);
                setProperty(response.response.data);
                if (savedMessage) {
                    initialMessage = { message: savedMessage, timestamp: new Date().toISOString(), is_response: true };
                    setAllMsg([initialMessage]);
                    localStorage.removeItem("initialMessage");
                    return;
                }

                // For buyer-seller chats: If this is a new property chat, don't fetch messages
                if (!isCustomerSupport && (props.sellerDetails?.isNewChat || (!props.selectedChat?.id && propertyId))) {
                    if (allMsg.length === 0) {
                        setAllMsg([]);
                    }
                    return;
                }
            }

            let requestUrl = `/messaging/customer-service-conversation`;
            let conversationResponse;
            let conversationId;

            if (props.selectedChat !== "customerSupport") {
                requestUrl = `/messaging/conversations/${props.selectedChat?.id}/messages`;
            } else {
                conversationResponse = await getApi(requestUrl);
                conversationId = conversationResponse.response?.data?.conversation_id;
                requestUrl = `/messaging/conversations/${conversationId}/messages`;
            }

            const response = await getApi(requestUrl);
            if (response?.error === 401) {
                navigate("/login");
            } else if (!response?.response) {
                throw new Error("Network response was not ok");
            } else {
                let result = response.response;

                result = result.data;
                const updatedMsgs = result.map((item) => {
                    if (item.message.includes("User Name:")) {
                        // Keep only the part before "User Name:"
                        const cleanedMessage = item.message.split("User Name:")[0].trim();
                        return { ...item, message: cleanedMessage };
                    }
                    // Return unchanged message
                    return item;
                });

                setAllMsg(updatedMsgs);
                scrolltoBottom();
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    // Different useEffect patterns for customer support vs buyer-seller
    useEffect(() => {
        if (propertyId) {
            fetchData();
        } else {
            setProperty(null);
        }
    }, [propertyId]);

    useEffect(() => {
        if (isCustomerSupport) {
            // Customer support logic
            fetchData();
            scrolltoBottom();
            if (props?.messageValue) {
                setTimeout(() => {
                    addMessage(props.messageValue, "textMessage");
                }, 100);
            }
        } else {
            // Buyer-seller logic
            if (props.sellerDetails && (!props.sellerDetails.isNewChat || propertyId)) {
                fetchData();
            }
        }
    }, [isCustomerSupport ? props.messageValue : props.sellerDetails]);

    // Separate useEffect for buyer-seller initial message handling
    useEffect(() => {
        if (!isCustomerSupport && props?.messageValue) {
            setTimeout(() => {
                addMessage(props.messageValue, "textMessage");
            }, 100);
        }
    }, [props.messageValue, isCustomerSupport]);

    // Auto-scroll when messages change
    useEffect(() => {
        if (allMsg.length > 0) {
            setTimeout(() => {
                scrolltoBottom();
            }, 100);
        }
    }, [allMsg]);

    // Auto-scroll when typing indicator changes (buyer-seller only)
    useEffect(() => {
        if (!isCustomerSupport && otherUserTyping) {
            setTimeout(() => {
                scrolltoBottom();
            }, 100);
        }
    }, [otherUserTyping, isCustomerSupport]);

    useEffect(() => {
        const mediaFileName = allMsg?.length > 0 && allMsg?.filter((ele) => ele.media && !ele.media.startsWith("/documents/"));
        const docs = allMsg?.length > 0 && allMsg?.filter((ele) => ele.message?.includes("/documents/") || ele.media?.startsWith("/documents/"));
        setShareFileNameDir(mediaFileName);
        setSharedDocuments(docs);

        // Additional scroll for buyer-seller
        if (!isCustomerSupport && allMsg.length > 0) {
            setTimeout(() => scrolltoBottom(), 100);
        }
    }, [allMsg, isCustomerSupport]);

    const onDownloadFile = (fileUrl, filename) => {
        const fullUrl = API_BASE_URL + fileUrl;

        // Create a hidden link element
        const link = document.createElement("a");
        link.href = fullUrl;
        link.setAttribute("download", filename || "download.pdf");
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const groupedMessages = useMemo(() => {
        const groups = {};
        const messagesToProcess = allMsg?.length > 0 ? allMsg : [];

        messagesToProcess?.forEach((message) => {
            let date;
            if (message.timestamp) {
                date = new Date(message.timestamp);
            } else {
                date = new Date();
            }

            // Create local date string in YYYY-MM-DD format
            const dateKey = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");

            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(message);
        });

        return Object.entries(groups)
            .map(([date, messages]) => ({
                date,
                messages,
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [allMsg]);

    const getDateLabel = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return "Today";
        } else if (date.toDateString() === yesterday.toDateString()) {
            return "Yesterday";
        } else {
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        }
    };

    const scrollToMessage = (messageId) => {
        try {
            const messageElement = document.getElementById(`message-${messageId}`);
            if (messageElement && messageElement.scrollIntoView) {
                messageElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                // Optional: Add highlight effect to the message
                messageElement.style.backgroundColor = "rgba(255, 255, 0, 0.2)";
                setTimeout(() => {
                    if (messageElement) {
                        messageElement.style.backgroundColor = "transparent";
                        messageElement.style.transition = "background-color 0.5s ease";
                    }
                }, 1500);
            }
        } catch (error) {
            console.warn("ScrollToMessage failed:", error);
        }
    };

    const gotoAllChats = () => {
        document.querySelector(".chats-sidebar-container").classList.add("visible-chat");
        document.querySelector(".chats-sidebar-container").classList.remove("hidden-chat");
        document.querySelector(".chats-container").classList.add("hidden-chat");
        document.querySelector(".chats-container").classList.remove("visible-chat");
        document.querySelector(".directory-main-container")?.classList?.add("hidden-chat");

        document.querySelector(".messaging-wrapper").classList.remove("hidden-chat");
    };

    const filterMessage = (messageText) => {
        return messageText?.includes("User Name:") ? messageText.split("User Name:")[0] : messageText;
    };

    // Buyer-seller specific functions
    const returnRecieverImage = () => {
        if (!isCustomerSupport) {
            if (props.sellerDetails.user_id !== profileData?.id) {
                return API_BASE_URL + props.sellerDetails.user_profile_image;
            } else {
                return API_BASE_URL + props.sellerDetails.receiver_user_profile_image;
            }
        }
        return customer_avatar;
    };

    // Helper function to get receiver's initials for display when no profile image
    const getReceiverInitials = () => {
        if (isCustomerSupport) return "CS";

        const receiverName = props.sellerDetails?.receiver_user_name || props.sellerDetails?.owner_name || "";
        const firstName = props.sellerDetails?.first_name || "";
        const lastName = props.sellerDetails?.last_name || "";

        if (firstName && lastName) {
            return `${firstName[0]?.toUpperCase() || ""}${lastName[0]?.toUpperCase() || ""}`;
        }

        if (receiverName) {
            const nameParts = receiverName.split(" ");
            if (nameParts.length >= 2) {
                return `${nameParts[0][0]?.toUpperCase() || ""}${nameParts[1][0]?.toUpperCase() || ""}`;
            }
            return receiverName[0]?.toUpperCase() || "?";
        }

        return "?";
    };

    // Check if receiver has a valid profile image
    const hasValidReceiverImage = () => {
        if (isCustomerSupport) return true;
        const imageUrl = returnRecieverImage();
        return imageUrl && !imageUrl.endsWith("null") && !imageUrl.endsWith("undefined");
    };

    // Get header title and image
    const getHeaderTitle = () => {
        if (isCustomerSupport) {
            return "Customer Service";
        }
        return props.sellerDetails?.receiver_user_name || props.sellerDetails?.owner_name;
    };

    const getHeaderImage = () => {
        if (isCustomerSupport) {
            return customer_avatar;
        }
        return returnRecieverImage();
    };

    const shouldShowPropertyInfo = () => {
        if (isCustomerSupport) {
            return property && property?.price;
        }
        return property && (property?.price || property?.address);
    };

    const getPropertyAddress = () => {
        if (isCustomerSupport) {
            return property.StreetNumber
                ? `${property.StreetNumber} ${property.StreetName} ${property.StreetSuffix}, ${property.City}, ${property.StateOrProvince} ${property.PostalCode}`
                : "";
        }
        return property.address ? property.address : "";
    };

    return (
        <React.Fragment>
            <div className="main-chat-container tw-overflow-y-auto">
                <div className={props.userSidebar ? "w-70 overflow-hidden position-relative " : "w-100 overflow-hidden position-relative"}>
                    {/* Chat Header */}
                    <div className="tw-flex tw-justify-between tw-border-b tw-border-background_secondary_color tw-px-4 tw-items-center tw-gap-2 py-3 tw-bg-white tw-h-[55px]">
                        <div className="tw-flex tw-gap-2 tw-items-center">
                            <IoIosArrowBack onClick={() => gotoAllChats()} className="tw-text-[24px] md:tw-hidden" />

                            {/* Dynamic Profile Image/Avatar */}
                            {!isCustomerSupport && hasValidReceiverImage() && !headerImageError ? (
                                <img
                                    className="tw-rounded-full tw-h-[35px] tw-w-[35px] tw-object-cover"
                                    src={getHeaderImage()}
                                    alt="profile"
                                    onError={() => setHeaderImageError(true)}
                                />
                            ) : (
                                <div className="tw-rounded-full tw-h-[35px] tw-w-[35px] tw-bg-primary tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold tw-text-white">
                                    {isCustomerSupport ? (
                                        <img className="tw-rounded-full tw-h-[35px] tw-w-[35px]" src={customer_avatar} alt="Customer Service" />
                                    ) : (
                                        getReceiverInitials()
                                    )}
                                </div>
                            )}

                            <p className="tw-font-semibold tw-text-[20px] tw-text-black">{getHeaderTitle()}</p>
                        </div>

                        {/* Header Actions */}
                        <div className="lg:tw-flex md:tw-flex tw-gap-4 mobile-hide tw-items-center">
                            {/* Connection Status Indicator */}
                            <div className="tw-flex tw-items-center tw-gap-2">
                                {connectionStatus === "connected" && (
                                    <div className="tw-flex tw-items-center tw-gap-1 tw-text-green-600 tw-text-xs">
                                        <div className="tw-w-2 tw-h-2 tw-bg-green-500 tw-rounded-full"></div>
                                        Connected
                                    </div>
                                )}
                                {connectionStatus === "reconnecting" && (
                                    <div className="tw-flex tw-items-center tw-gap-1 tw-text-yellow-600 tw-text-xs">
                                        <div className="tw-w-2 tw-h-2 tw-bg-yellow-500 tw-rounded-full tw-animate-pulse"></div>
                                        Reconnecting... ({reconnectAttempt}/{maxReconnectAttempts})
                                    </div>
                                )}
                                {connectionStatus === "disconnected" && !isReconnecting && (
                                    <div
                                        onClick={manualReconnect}
                                        className="tw-flex tw-items-center tw-gap-1 tw-text-red-600 tw-text-xs tw-cursor-pointer tw-hover:tw-text-red-800"
                                        title="Click to reconnect"
                                    >
                                        <div className="tw-w-2 tw-h-2 tw-bg-red-500 tw-rounded-full"></div>
                                        Disconnected (Click to reconnect)
                                    </div>
                                )}
                            </div>

                            <ChatSearch messages={allMsg} onMessageSelect={scrollToMessage} />
                            {(shareFileNameDir?.length > 0 || props?.sellerDetails?.external_property_id || sharedDocuments?.length > 0) && (
                                <div
                                    onClick={() => {
                                        setOpenMedia(true);
                                        fetchData();
                                    }}
                                    className="tw-flex tw-items-center tw-cursor-pointer tw-text-[14px] tw-gap-2 tw-justify-center tw-bg-background_color tw-rounded-md tw-p-2 tw-px-4"
                                >
                                    <i className="fi fi-rr-folder tw-mt-1"></i>
                                    Files & Prop Info
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            height: "calc(100vh - 110px)",
                        }}
                        className="tw-flex tw-flex-col tw-w-full justify-content-between"
                    >
                        <div className="messaging-wrapper tw-flex tw-flex-col tw-h-full tw-relative tw-rounded-tr-xl">
                            {/* Property Info Banner */}
                            {shouldShowPropertyInfo() && (
                                <div className="tw-flex tw-justify-between tw-items-center tw-px-3 tw-mb-0 tw-font-roboto tw-border-b tw-bg-[#fff] tw-p-2 font-roboto font-normal font-medium text-[10.8px] leading-[19px] text-[#000000] tw-gap-2 tw-text-[12px] tw-py-[2px] tw-border-r border-gray-500">
                                    <div className="tw-flex tw-flex-col">
                                        <div className="tw-font-semibold tw-text-[18px] tw-text-[#666666]">{getPropertyAddress()}</div>
                                        <div className="tw-flex tw-flex-row tw-gap-4 tw-w-full tw-text-[#666666] tw-font-semibold">
                                            <div className="tw-flex tw-gap-1 tw-items-center tw-text-[14px]">
                                                <IoPricetagsSharp className="tw-flex tw-text-primary" /> ${formatUSAPrice(property?.price) || "N/A"}
                                            </div>
                                            <div className="tw-flex tw-gap-1 tw-items-center tw-text-[14px]">
                                                <BedOutlinedIcon className="tw-text-primary tw-ml-2" /> {property?.beds} beds
                                            </div>
                                            <div className="tw-flex tw-gap-1 tw-items-center tw-text-[14px]">
                                                <ShowerOutlinedIcon className="tw-text-primary tw-ml-2" /> {property?.baths} baths
                                            </div>
                                            <div className="tw-flex tw-gap-1 tw-items-center tw-text-[14px]">
                                                <StraightenOutlinedIcon className="tw-ml-2 tw-text-primary" /> {property?.LivingArea} sqft
                                            </div>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/preview/${property.ListingId}?from=chat${props.sellerDetails?.id ? "-" + props.sellerDetails?.id : ""}`}
                                        className="tw-flex tw-items-center tw-justify-center tw-bg-[#EAEEF4] tw-h-[40px] tw-w-[40px] tw-rounded-full"
                                    >
                                        <BiLinkExternal size={18} className="tw-text-black" />
                                    </Link>
                                </div>
                            )}

                            {/* Messages Container */}
                            <div
                                ref={ref}
                                className="tw-bg-[#FFFFFF] tw-h-full tw-overflow-y-auto tw-w-full tw-scrollbar-thin tw-scrollbar-corner-transparent tw-scrollbar-thumb-primary tw-scrollbar-track-transparent"
                            >
                                {groupedMessages.map((group, groupIndex) => (
                                    <div key={groupIndex} className="message-group">
                                        {/* Date Separator */}
                                        <div className="tw-flex tw-justify-center tw-my-4">
                                            <div className="tw-text-medium_text tw-px-4 tw-py-2 tw-rounded-full tw-text-sm">
                                                {getDateLabel(group.date)}
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        {group.messages.map((item, index) => {
                                            return (
                                                <MessageItem
                                                    key={index}
                                                    item={item}
                                                    index={index}
                                                    profileData={profileData}
                                                    onDownloadFile={onDownloadFile}
                                                    filterMessage={filterMessage}
                                                    isCustomerSupport={isCustomerSupport}
                                                    sellerDetails={props.sellerDetails}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}

                                {/* Typing indicator (buyer-seller only) */}
                                {!isCustomerSupport && otherUserTyping && (
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-mb-2">
                                        <img className="tw-rounded-full tw-h-[25px] tw-w-[25px]" src={returnRecieverImage()} alt="User avatar" />
                                        <div className="tw-bg-gray-200 tw-rounded-lg tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-1">
                                            <span className="tw-text-sm tw-text-gray-600">
                                                {props.sellerDetails?.receiver_user_name || props.sellerDetails?.owner_name} is typing
                                            </span>
                                            <div className="tw-flex tw-gap-1">
                                                <div
                                                    className="tw-w-1 tw-h-1 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
                                                    style={{ animationDelay: "0ms" }}
                                                ></div>
                                                <div
                                                    className="tw-w-1 tw-h-1 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
                                                    style={{ animationDelay: "150ms" }}
                                                ></div>
                                                <div
                                                    className="tw-w-1 tw-h-1 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
                                                    style={{ animationDelay: "300ms" }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Chat Input */}
                            <ChatInput
                                onaddMessage={addMessage}
                                setMedia_image={setMedia_image}
                                ws={!isCustomerSupport ? ws : undefined}
                                conversationId={!isCustomerSupport ? props.sellerDetails?.id : undefined}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {openMedia && (
                <MediaDocumentModal
                    onCancel={() => {
                        setOpenMedia(false);
                    }}
                    shareFile={shareFileNameDir}
                    sharedDocuments={sharedDocuments}
                    onConfirm={() => setOpenMedia(false)}
                    property={property}
                />
            )}
            {selectedImage && <ImagePreviewModal isOpen={!!selectedImage} onClose={() => setSelectedImage(null)} imageUrl={selectedImage} />}
        </React.Fragment>
    );
}

export default UnifiedChat;
